<script setup lang="ts">
import { Link } from '@inertiajs/vue3'
import {
  Activity,
  Briefcase,
  Building2,
  Building,
  FileBarChart,
  FileText,
  FormIcon,
  History,
  Landmark,
  LayoutDashboardIcon,
  LayoutGrid,
  ListTodo,
  Mail,
  ShieldAlert,
  Terminal,
  UserCog,
  Users,
} from 'lucide-vue-next'
import { computed } from 'vue'

import NavFooter from '@/components/NavFooter.vue'
import NavMain from '@/components/NavMain.vue'
import NavUser from '@/components/NavUser.vue'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar'
import { useAuth } from '@/composables/useAuth'
import {
  applyPagePath,
  commercialEnquiriesPath,
  contactMessagesPath,
  customersPath,
  dashboardPath,
  equifaxScoreRequestsPath,
  legacyApiLogsPath,
  lenderApiLogsPath,
  lendersPath,
  loanApplicationsPath,
  organizationsPath,
  partnersPath,
  portalRootPath,
  reportsPath,
  usersPath,
} from '@/routes'
import { type NavItem } from '@/types'

import AppLogo from './AppLogo.vue'

const { isAdmin } = useAuth()

const mainNavItems = computed<NavItem[]>(() => [
  {
    title: 'Dashboard',
    href: dashboardPath(),
    icon: LayoutGrid,
  },
  {
    title: 'Organizations',
    href: organizationsPath(),
    icon: Building2,
  },
  {
    title: 'Partners',
    href: partnersPath(),
    icon: Building,
  },
  {
    title: 'Lenders',
    href: lendersPath(),
    icon: Landmark,
  },
  {
    title: 'Customers',
    href: customersPath(),
    icon: Users,
  },
  {
    title: 'Applications',
    href: loanApplicationsPath(),
    icon: FileText,
  },
])

const footerNavItems: NavItem[] = [
  {
    title: 'Reports',
    href: reportsPath(),
    icon: FileBarChart,
    external: false,
  },
  {
    title: 'Staff',
    href: usersPath(),
    icon: UserCog,
    external: false,
  },
  {
    title: 'Failed Credit Checks',
    href: equifaxScoreRequestsPath(),
    icon: ShieldAlert,
    external: false,
  },
  {
    title: 'Lender API Logs',
    href: lenderApiLogsPath(),
    icon: Activity,
    external: false,
  },
  {
    title: 'Legacy API Logs',
    href: legacyApiLogsPath(),
    icon: History,
    external: false,
  },
  {
    title: 'Commercial Enquiries',
    href: commercialEnquiriesPath(),
    icon: Briefcase,
    external: false,
  },
  {
    title: 'Contact Messages',
    href: contactMessagesPath(),
    icon: Mail,
    external: false,
  },
  ...(import.meta.env.DEV
    ? [
        {
          title: 'Dev Logs',
          href: '/dev_logs',
          icon: Terminal,
          external: false,
        },
      ]
    : []),
  {
    title: 'Job Queue',
    href: '/jobs',
    icon: ListTodo,
    external: true,
  },
  {
    title: 'Portal',
    href: portalRootPath(),
    icon: LayoutDashboardIcon,
  },
  {
    title: 'Apply Form',
    href: applyPagePath(),
    icon: FormIcon,
  },
]
</script>

<template>
  <Sidebar collapsible="icon" variant="inset">
    <SidebarHeader>
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton size="lg" as-child>
            <Link :href="dashboardPath()">
              <AppLogo />
            </Link>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarHeader>

    <SidebarContent>
      <NavMain :items="mainNavItems" />
    </SidebarContent>

    <SidebarFooter>
      <NavFooter v-if="isAdmin" :items="footerNavItems" />
      <NavUser />
    </SidebarFooter>
  </Sidebar>
  <slot />
</template>
